import { useAtom, useAtomValue } from "jotai";
import { useCallback } from "react";
import {
  panelCollapsedAtom,
  panelVariantAtom,
  panelViewAtom,
  selectedCardAtom,
  showHelpModalAtom,
  type SelectedCardData,
} from "@/store/bookingStore";
import { selectModeAtom } from "@/store/selectionStore";

/**
 * Centralized UI state for the booking page.
 * Keeps selected card, panel collapse, help modal, and derived panel styling in one place.
 */
export function useBookingUiState() {
  const [selectedCard, setSelectedCard] = useAtom(selectedCardAtom);
  const [panelCollapsed, setPanelCollapsed] = useAtom(panelCollapsedAtom);
  const [showHelpModal, setShowHelpModal] = useAtom(showHelpModalAtom);

  const selectMode = useAtomValue(selectModeAtom);
  const panelVariant = useAtomValue(panelVariantAtom);
  const panelView = useAtomValue(panelViewAtom);

  const openSelectedCard = useCallback(
    (card: SelectedCardData) => {
      setSelectedCard(card);
      setPanelCollapsed(false);
    },
    [setPanelCollapsed, setSelectedCard],
  );

  const closeSelectedCard = useCallback(() => {
    setSelectedCard(null);
    setPanelCollapsed(false);
  }, [setPanelCollapsed, setSelectedCard]);

  const togglePanelCollapsed = useCallback(() => {
    setPanelCollapsed((prev) => !prev);
  }, [setPanelCollapsed]);

  return {
    // state
    selectedCard,
    panelCollapsed,
    showHelpModal,

    // derived
    selectMode,
    panelVariant,
    panelView,

    // setters
    setSelectedCard,
    setPanelCollapsed,
    setShowHelpModal,

    // helpers
    openSelectedCard,
    closeSelectedCard,
    togglePanelCollapsed,
  };
}
